import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { CompanyDraft, SelectData } from "../../../../models";
import { CreateCollaboratorRequest, UpdateCollaboratorRequest } from "../../../../service/organisation";
import { createCollaborator, updateCollaborator } from "../../../../service/organisation/OrganisationService";
import { lookUp, SearchCollaboratorsResponse, searchDevelopers } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";

type FormField =
  | "name"
  | "collaboratorType"
  | "description"
  | "email"
  | "phone"
  | "website"
  | "crmId"
  | "linkedOrganisationUuid"
  | "addressLine1"
  | "addressLine2"
  | "addressCity"
  | "addressRegion"
  | "addressPostcode"
  | "addressCountryCode";

interface AddEditCompanyModalState {
  key: string;
  name: string;
  collaboratorType: string;
  description: string;
  email: string;
  phone: string;
  website: string;
  crmId: string;
  linkedOrganisationUuid: string;
  addressLine1: string;
  addressLine2: string;
  addressCity: string;
  addressRegion: string;
  addressPostcode: string;
  addressCountryCode: string;
}

interface UseAddEditCompanyModalProps {
  onClose: () => void;
  onSaved?: (created?: { uuid: string; name: string }) => void;
  show: boolean;
  mode?: "add" | "edit";
  initialName?: string;
  initialCollaboratorType?: string;
  companyUuid?: string | null;
  fetchCompanyByUuid?: (companyUuid: string) => Promise<SearchCollaboratorsResponse["results"][number] | null>;
  submitMode?: "api" | "deferred";
  onDeferredSave?: (draft: CompanyDraft) => void;
}

interface UseAddEditCompanyModalReturn {
  formState: AddEditCompanyModalState;
  countryOptions: SelectData;
  developerList: { key: string; value: string }[];
  errors: ServiceError[];
  isSaving: boolean;
  updateField: (field: FormField, value: string) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  handleCancel: () => void;
  onDeveloperTypeSearch: (value: string) => void;
  onDeveloperSelect: (value: string) => void;
  company: SearchCollaboratorsResponse["results"][number] | null;
  isCompanyLoading: boolean;
}

const defaultState: AddEditCompanyModalState = {
  key: uuidv4(),
  name: "",
  collaboratorType: "",
  description: "",
  email: "",
  phone: "",
  website: "",
  crmId: "",
  linkedOrganisationUuid: "",
  addressLine1: "",
  addressLine2: "",
  addressCity: "",
  addressRegion: "",
  addressPostcode: "",
  addressCountryCode: "",
};

const toNullableString = (value: string): string | null => (value.trim() === "" ? null : value.trim());

const toAssociationList = (): CreateCollaboratorRequest["associations"] => [];
const toUpdateAssociationList = (): UpdateCollaboratorRequest["associations"] => [];
const mapCompanyToState = (
  company?: SearchCollaboratorsResponse["results"][number] | null
): AddEditCompanyModalState => ({
  key: "",
  name: company?.name ?? "",
  collaboratorType: company?.collaboratorType ?? "",
  description: company?.description ?? "",
  email: company?.email ?? "",
  phone: company?.phone ?? "",
  website: company?.website ?? "",
  crmId: company?.externalReference ?? "",
  linkedOrganisationUuid: company?.organisationUuid ?? "",
  addressLine1: company?.addressLine1 ?? "",
  addressLine2: company?.addressLine2 ?? "",
  addressCity: company?.addressCity ?? "",
  addressRegion: company?.addressRegion ?? "",
  addressPostcode: company?.addressPostcode ?? "",
  addressCountryCode: company?.addressCountryCode ?? "",
});

export const useAddEditCompanyModal = ({
  onClose,
  onSaved,
  show,
  mode = "add",
  initialName,
  initialCollaboratorType,
  companyUuid = null,
  fetchCompanyByUuid,
  submitMode = "api",
  onDeferredSave,
}: UseAddEditCompanyModalProps): UseAddEditCompanyModalReturn => {
  const [formState, setFormState] = useState<AddEditCompanyModalState>(defaultState);
  const [countryOptions, setCountryOptions] = useState<SelectData>([]);
  const [developerList, setDeveloperList] = useState<{ key: string; value: string }[]>([]);
  const [errors, setErrors] = useState<ServiceError[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [company, setCompany] = useState<SearchCollaboratorsResponse["results"][number] | null>(null);
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);

  const updateField = useCallback((field: FormField, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const getDevelopers = useCallback(async (search: string) => {
    const response = await searchDevelopers({
      paging: {
        limit: 20,
        beforeCursor: "",
        afterCursor: "",
      },
      filter: {
        results: {
          displayName: {
            operator: "startsWith",
            value: search,
          },
        },
      },
      excludedProjectUuid: null,
    });

    if (response.status === Status.Success && response.data?.results) {
      const list = response.data.results.flatMap((el) => {
        if (el.parentOrganisation && el.parentOrganisation.uuid && el.displayName) {
          return { key: el.parentOrganisation.uuid, value: el.displayName };
        }
        return [];
      });
      setDeveloperList(list);
    }
  }, []);

  useEffect(() => {
    getDevelopers("");
  }, [getDevelopers]);

  useEffect(() => {
    if (show) {
      const nextState =
        mode === "edit"
          ? mapCompanyToState(company)
          : {
              ...defaultState,
              name: initialName ?? "",
              collaboratorType: initialCollaboratorType ?? "",
            };
      setFormState(nextState);
      setErrors([]);
    }
  }, [company, initialCollaboratorType, initialName, mode, show]);

  useEffect(() => {
    let isActive = true;
    if (mode !== "edit" || !show || !companyUuid || !fetchCompanyByUuid) {
      setCompany(null);
      setIsCompanyLoading(false);
      return () => {
        isActive = false;
      };
    }

    setCompany(null);
    setIsCompanyLoading(true);
    fetchCompanyByUuid(companyUuid)
      .then((result) => {
        if (!isActive) return;
        setCompany(result);
      })
      .finally(() => {
        if (isActive) setIsCompanyLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [companyUuid, fetchCompanyByUuid, mode, show]);

  useEffect(() => {
    const fetchCountries = async (): Promise<void> => {
      const countriesRes = await lookUp({ type: "Country", sort: [{ key: "valueString", direction: "asc" }] });
      if (countriesRes.status === Status.Success && countriesRes.data) {
        const ukFirst = [...countriesRes.data].sort((a, b) => {
          const aIsUk = a.key.includes("GB");
          const bIsUk = b.key.includes("GB");
          if (aIsUk && !bIsUk) return -1;
          if (!aIsUk && bIsUk) return 1;
          return 0;
        });
        setCountryOptions(
          ukFirst.map((country) => ({
            key: country.key,
            value: country.valueString,
          }))
        );
      }
    };

    fetchCountries();
  }, []);

  const basePayload = useMemo<CreateCollaboratorRequest>(
    () => ({
      addressCity: toNullableString(formState.addressCity),
      addressCountryCode: toNullableString(formState.addressCountryCode),
      addressLine1: toNullableString(formState.addressLine1),
      addressLine2: toNullableString(formState.addressLine2),
      addressPostcode: toNullableString(formState.addressPostcode),
      addressRegion: toNullableString(formState.addressRegion),
      associations: toAssociationList(),
      collaboratorType: formState.collaboratorType.trim(),
      description: formState.description.trim(),
      email: toNullableString(formState.email),
      externalParty: null,
      externalReference: toNullableString(formState.crmId),
      name: formState.name.trim(),
      organisationUuid: toNullableString(formState.linkedOrganisationUuid) ?? company?.organisationUuid ?? null,
      phone: toNullableString(formState.phone),
      website: toNullableString(formState.website),
      validateOnly: false,
    }),
    [company?.organisationUuid, formState]
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // This form renders inside another one in places, and React bubbles submits up the fiber tree.
      event.stopPropagation();
      const nextErrors: ServiceError[] = [];
      const trimmedName = formState.name.trim();
      const trimmedDescription = formState.description.trim();
      const trimmedCollaboratorType = formState.collaboratorType.trim();

      if (!trimmedName) {
        nextErrors.push({ code: "REQUIRED_NAME", message: "Name is required" });
      }
      if (trimmedName.length > 100) {
        nextErrors.push({ code: "MAX_LENGTH_NAME", message: "Name must be 100 characters or fewer" });
      }
      if (!trimmedCollaboratorType) {
        nextErrors.push({ code: "REQUIRED_COLLABORATOR_TYPE", message: "Company type is required" });
      }
      if (!trimmedDescription) {
        nextErrors.push({ code: "REQUIRED_DESCRIPTION", message: "Description is required" });
      }

      if (nextErrors.length > 0) {
        setErrors(nextErrors);
        return;
      }

      if (submitMode === "deferred") {
        setErrors([]);
        onDeferredSave?.(formState);
        setFormState(defaultState);
        onSaved?.();
        onClose();
        return;
      }

      setIsSaving(true);
      setErrors([]);

      const organisationUuidForUpdate =
        toNullableString(formState.linkedOrganisationUuid) ?? company?.organisationUuid ?? "";

      const request =
        mode === "edit" && company
          ? updateCollaborator({
              ...basePayload,
              associations: toUpdateAssociationList(),
              organisationUuid: organisationUuidForUpdate,
              uuid: company.uuid,
              rowVersion: company.rowVersion,
            })
          : createCollaborator(basePayload);

      request
        .then((response: Awaited<ReturnType<typeof createCollaborator>>) => {
          if (response.status === Status.Error) {
            setErrors(response.errors || []);
            return;
          }
          /*
            Only a create reports the company back: an edit has no new uuid to hand over, and the response of
            an update carries none. The name comes from the form rather than the response, which returns only
            the uuid and row version.
          */
          const created =
            mode !== "edit" && response.data?.uuid
              ? { uuid: response.data.uuid, name: formState.name.trim() }
              : undefined;
          onSaved?.(created);
          setFormState(defaultState);
          onClose();
        })
        .finally(() => {
          setIsSaving(false);
        });
    },
    [basePayload, company, formState, mode, onClose, onDeferredSave, onSaved, submitMode]
  );

  const handleCancel = useCallback(() => {
    setErrors([]);
    onClose();
  }, [onClose]);

  const onDeveloperTypeSearch = useCallback(
    (value: string) => {
      getDevelopers(value);
    },
    [getDevelopers]
  );

  const onDeveloperSelect = useCallback(
    (value: string) => {
      updateField("linkedOrganisationUuid", value);
    },
    [updateField]
  );

  return {
    formState,
    countryOptions,
    developerList,
    errors,
    isSaving,
    updateField,
    handleSubmit,
    handleCancel,
    onDeveloperTypeSearch,
    onDeveloperSelect,
    company,
    isCompanyLoading,
  };
};
